import { create } from 'zustand'
import { IS_DEV } from '@/common/env'
import { SIX_LOCAL_DEV_FLAGS } from '@/common/storage/keys'
import { localFlagDefinitions } from './localFlagDefinitions'

type FlagValue = boolean | string
type LocalFlagValues = Record<string, FlagValue>

interface LocalFlagState {
    values: LocalFlagValues
    setLocalFlag: (key: string, value: FlagValue) => void
}

const getDefaults = (): LocalFlagValues =>
    localFlagDefinitions.reduce((acc, def) => ({ ...acc, [def.key]: def.default }), {} as LocalFlagValues)

const getInitialValues = (): LocalFlagValues => {
    const defaults = getDefaults()
    if (!IS_DEV || typeof window === 'undefined') return defaults
    try {
        const raw = localStorage.getItem(SIX_LOCAL_DEV_FLAGS)
        const stored = raw ? (JSON.parse(raw) as LocalFlagValues) : {}
        return { ...defaults, ...stored }
    } catch {
        return defaults
    }
}

const persistValues = (values: LocalFlagValues) => {
    if (!IS_DEV || typeof window === 'undefined') return
    try {
        localStorage.setItem(SIX_LOCAL_DEV_FLAGS, JSON.stringify(values))
    } catch {
        // ignore
    }
}

const useLocalFlagStore = create<LocalFlagState>((set, get) => ({
    values: getInitialValues(),
    setLocalFlag: (key, value) => {
        const values = { ...get().values, [key]: value }
        persistValues(values)
        set({ values })
    },
}))

export function useLocalFlags(): LocalFlagValues {
    return useLocalFlagStore((s) => s.values)
}

export default useLocalFlagStore
